import { useCallback, useState } from 'react';
import { api } from 'services/api-client';
import { useToast } from '../../contexts/ToastContext';
import { useExercisesStore } from '../../stores/useExercisesStore';
import { IExercise } from 'types/workout/exercise';

export interface QuickCreatedExercise {
  id: number;
  name: string;
}

/**
 * Hook para crear un ejercicio rapidamente con el nombre como unico campo
 * requerido. Pensado para los dos entry points del flujo de import IA:
 *  - "+ Crear '<query>'" inline en el dropdown del ejercicio.
 *  - "+ Agregar ejercicio" en el header de NewCreatePlan.
 *
 * Despues de crear, el ejercicio queda en el store de Zustand para que
 * cualquier dropdown abierto lo vea sin refetchear.
 */
export const useQuickCreateExercise = () => {
  const { showToast } = useToast();
  const addExercise = useExercisesStore().addExercise;
  const [loading, setLoading] = useState<boolean>(false);

  const createExercise = useCallback(
    async (name: string): Promise<QuickCreatedExercise | null> => {
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        showToast('warn', 'Nombre requerido', 'Escribe un nombre para el ejercicio');
        return null;
      }
      setLoading(true);
      try {
        const response = await api.exercise.createExercise({
          name: trimmed,
          multimedia: '',
          createdByCoach: true,
          createdByAdmin: false
        });
        const created = ((response as { data?: unknown }).data ?? response) as IExercise;
        addExercise(created);
        showToast('success', 'Ejercicio creado', `"${created.name}" agregado a tu catálogo`);
        return { id: created.id, name: created.name };
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Error desconocido';
        showToast('error', 'No se pudo crear el ejercicio', detail);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [showToast, addExercise]
  );

  return { loading, createExercise };
};
